import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, X, Plus, Trash2, Save, LogOut, Lock, Star, ExternalLink, MapPin, Camera, ImageIcon, Minus } from 'lucide-react';
import { OrderCart, CART_I18N, cartKey } from '../components/menu/OrderCart';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  'Viandes': '🥩', 'Burgers': '🍔', 'Frites': '🍟', 'Mitraillettes': '🌯',
  'Box Enfant': '🎁', 'Specialites': '⭐', 'Brochettes': '🍢', 'Assiettes': '🍽️',
  'Sauces': '🫙', 'Alcools': '🍷', 'Boissons': '🥤', 'Supplements': '➕',
  'Softs': '🥤', 'Bieres': '🍺', 'Cafes-Thes': '☕', 'Glaces': '🍨',
  'Offres': '🎉', 'Desserts': '🍰',
  'Infos & Formules': '📋', 'Les Fromageres': '🧀', 'Les Poissonnieres': '🐟',
  'Les Calzones': '🥟', 'Pizzas Desserts': '🍫', 'Les Classiques': '🍕',
  'Les Speciales': '🌟', 'Les Viandes': '🥩', 'Les Vegetariennes': '🥗',
  'Sandwichs du Monde': '🌍', 'Bubbles Salees': '🫧', 'Wraps': '🌯',
  'Planche': '🍽️', 'Salades a Composer': '🥗', 'Sandwichs Classiques': '🥖',
  'Croques': '🥪', 'Tartines': '🍞', 'Pates Fraiches': '🍝',
  'Omelettes': '🍳', 'Petits-Dejeuners / Brunch': '🥐',
  'Boissons Chaudes': '☕', 'Cocktails': '🍸', 'Cocktails Sans Alcool': '🥂',
  'Vins Maison': '🍷', 'Vins': '🍷', 'Digestifs': '🥃', 'Champagne': '🍾',
  'Salades & Mezzés Froids': '🥗', 'Mezzés Chauds & Accompagnements': '🫕', 'Grillades & Plats': '🔥',
  'Sandwichs': '🥙', 'Boissons Fraîches': '🧃',
  'Cocktails Signature': '🍹', 'Cocktails Classiques': '🍸', 'Bières au Fût': '🍺', 'Bières en Bouteille': '🍻',
  'Bulles': '🍾', 'Apéritifs': '🥂', 'Whiskies': '🥃', 'Rhums': '🥃', 'Gins': '🍸', 'Vodkas': '🍸',
  'Liqueurs & Digestifs': '🥃', 'Shots': '🥃',
  'Entrées & Découvertes': '🥗', 'Nos Plats': '🍽️', 'Accompagnements': '🍟',
  'Vins Blancs · Portugal': '🥂', 'Vin Rosé · Portugal': '🌸', 'Vins Rouges · Portugal': '🍷'
};

function FacebookIcon({ size = 20 }) {
  return <svg width={size} height={size} viewBox="0 0 48 48"><linearGradient id="fb1" x1="9.99" x2="40.62" y1="9.99" y2="40.62" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#2aa4f4"/><stop offset="1" stopColor="#007ad9"/></linearGradient><path fill="url(#fb1)" d="M24 4C12.954 4 4 12.954 4 24s8.954 20 20 20 20-8.954 20-20S35.046 4 24 4z"/><path fill="#fff" d="M26.707 29.301h5.176l.813-5.258h-5.989v-2.874c0-2.184.714-4.121 2.757-4.121H32.8V12.41c-.483-.066-1.509-.217-3.451-.217-4.11 0-6.522 2.162-6.522 7.1v4.749h-4.643v5.258h4.643V44.04c.927.138 1.868.214 2.827.214.894 0 1.77-.066 2.633-.182V29.301z"/></svg>;
}
function GoogleIcon({ size = 20 }) {
  return <svg width={size} height={size} viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>;
}
function InstagramIcon({ size = 20 }) {
  return <svg width={size} height={size} viewBox="0 0 48 48"><radialGradient id="mig1" cx="19.38" cy="42.04" r="44.9" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#fd5"/><stop offset=".328" stopColor="#ff543f"/><stop offset=".348" stopColor="#fc5245"/><stop offset=".504" stopColor="#e64771"/><stop offset=".643" stopColor="#d53e91"/><stop offset=".761" stopColor="#cc39a4"/><stop offset=".841" stopColor="#c837ab"/></radialGradient><path fill="url(#mig1)" d="M34.017 41.99l-20 .019c-4.4.004-8.003-3.592-8.008-7.992l-.019-20c-.004-4.4 3.592-8.003 7.992-8.008l20-.019c4.4-.004 8.003 3.592 8.008 7.992l.019 20c.005 4.401-3.592 8.004-7.992 8.008z"/><path fill="#fff" d="M24,31c-3.859,0-7-3.14-7-7s3.141-7,7-7s7,3.14,7,7S27.859,31,24,31z M24,19c-2.757,0-5,2.243-5,5s2.243,5,5,5s5-2.243,5-5S26.757,19,24,19z"/><circle cx="31.5" cy="16.5" r="1.5" fill="#fff"/><path fill="#fff" d="M30,37H18c-3.859,0-7-3.14-7-7V18c0-3.86,3.141-7,7-7h12c3.859,0,7,3.14,7,7v12C37,33.86,33.859,37,30,37z M18,13c-2.757,0-5,2.243-5,5v12c0,2.757,2.243,5,5,5h12c2.757,0,5-2.243,5-5V18c0-2.757-2.243-5-5-5H18z"/></svg>;
}
function TikTokIcon({ size = 20 }) {
  return <svg width={size} height={size} viewBox="0 0 48 48"><path d="M38.4 21.68V16c-2.66 0-4.69-.71-6.33-2.06A10.26 10.26 0 0 1 28 8h-5.34v22.42a5.15 5.15 0 1 1-3.52-4.88v-5.48a10.53 10.53 0 0 0-1.62-.13A10.49 10.49 0 0 0 7.03 30.32a10.49 10.49 0 0 0 10.49 10.49 10.49 10.49 0 0 0 10.49-10.49V19.84A15.25 15.25 0 0 0 38.4 21.68z" fill="#000"/></svg>;
}
function SnapchatIcon({ size = 20 }) {
  return <svg width={size} height={size} viewBox="0 0 48 48"><path fill="#fffc00" d="M24 4C12.954 4 4 12.954 4 24s8.954 20 20 20 20-8.954 20-20S35.046 4 24 4z"/><path fill="#fff" d="M33.5 27.2c-3.2.9-4.6 4.5-4.7 4.7-.2.4-.4.6-1.2.6-.5 0-1-.2-1.6-.4-.5-.2-1-.3-1.5-.3-.4 0-.9.1-1.4.3-.6.2-1.1.4-1.6.4-.8 0-1-.2-1.2-.6-.1-.2-1.5-3.8-4.7-4.7-.5-.1-.8-.5-.7-1 .1-.4.5-.7 1-.7 2.4.3 4.1 1.5 4.1 1.5.4.2.7.1.8-.1.1-.2.1-.5-.2-.8-1.6-1.5-1.7-3.2-1.7-3.8v-3.2c0-3 2.5-5.5 5.5-5.5 3 0 5.5 2.5 5.5 5.5v3.2c0 .6-.1 2.3-1.7 3.8-.3.3-.3.6-.2.8.1.2.4.3.8.1 0 0 1.7-1.2 4.1-1.5.5-.1.9.3 1 .7.1.5-.2.9-.7 1z"/></svg>;
}

const THEMES = {
  dark: {
    bg: '#1a1a1a', bgHeader: 'rgba(26,26,26,0.95)',
    cardClass: 'border-amber-900/20 bg-gradient-to-r from-[#222222] to-[#1e1e1e] hover:border-amber-700/40',
    text: 'text-amber-50', textSub: 'text-gray-500', textHead: 'text-amber-100', textPrice: 'text-amber-500',
    tabActive: 'bg-amber-600 text-white', tabInactive: 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white',
    adminBtn: 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10',
    editBannerBg: '', editBannerBorder: 'border-amber-600/30', editBannerText: 'text-amber-200',
    saveBtn: 'bg-emerald-600 hover:bg-emerald-700',
    closeBtn: 'bg-white/10 text-gray-300', logoutBtn: 'bg-red-500/20 text-red-400',
    inputBorder: 'border-white/10 focus:border-amber-500',
    modalBg: '#222', modalText: 'text-white', modalInputClass: 'bg-white/5 border-white/10 text-white focus:border-amber-500',
    modalBtnStyle: { background: '#D97706' },
    footerText: 'text-amber-200', footerSub: 'text-gray-500', footerMuted: 'text-gray-600',
    footerLine: 'linear-gradient(to right, transparent, #F59E0B, transparent)',
    linkCard: 'border-amber-900/30 bg-gradient-to-r from-[#252525] to-[#1e1e1e] hover:border-amber-600/50',
    linkText: 'text-amber-100', headingFont: 'Outfit', bodyFont: 'Outfit',
    ring: 'ring-amber-700/50', tabsBorder: 'border-amber-900/20', tabsBg: 'rgba(26,26,26,0.98)',
    imgPlaceholder: 'bg-white/5 border-white/10 text-gray-600',
    uploadBtn: 'bg-amber-600/20 text-amber-400 hover:bg-amber-600/30',
  },
  light: {
    bg: '#FDFBF7', bgHeader: 'rgba(253,251,247,0.85)',
    cardClass: 'border-[#3A2E28]/5 bg-white shadow-[0_2px_12px_rgba(58,46,40,0.04)] hover:shadow-[0_4px_20px_rgba(58,46,40,0.08)]',
    text: 'text-[#3A2E28]', textSub: 'text-[#7A685D]', textHead: 'text-[#3A2E28]', textPrice: 'text-[#D48C70]',
    tabActive: 'bg-[#3A2E28] text-white shadow-md', tabInactive: 'bg-white text-[#7A685D] border border-[#3A2E28]/8 hover:bg-[#F5EBE1]',
    adminBtn: 'bg-[#3A2E28]/5 text-[#7A685D] hover:bg-[#3A2E28]/10',
    editBannerBg: '#FFF5EE', editBannerBorder: 'border-[#D48C70]/20', editBannerText: 'text-[#D48C70]',
    saveBtn: '',
    closeBtn: 'bg-[#3A2E28]/5 text-[#7A685D]', logoutBtn: 'bg-red-50 text-red-400',
    inputBorder: 'border-[#3A2E28]/10 focus:border-[#D48C70]',
    modalBg: '#FDFBF7', modalText: 'text-[#3A2E28]', modalInputClass: 'bg-white border-[#3A2E28]/10 text-[#3A2E28] focus:border-[#D48C70] focus:ring-2 focus:ring-[#D48C70]/20',
    modalBtnStyle: { background: 'linear-gradient(135deg, #D48C70, #E6B981)' },
    footerText: 'text-[#3A2E28]', footerSub: 'text-[#7A685D]', footerMuted: 'text-[#C4B5A8]',
    footerLine: 'linear-gradient(to right, transparent, #D48C70, transparent)',
    linkCard: 'border-[#3A2E28]/8 bg-white shadow-sm hover:shadow-md',
    linkText: 'text-[#3A2E28]', headingFont: "'Playfair Display'", bodyFont: 'Outfit',
    ring: 'ring-white', tabsBorder: 'border-[#3A2E28]/5', tabsBg: 'rgba(253,251,247,0.95)',
    imgPlaceholder: 'bg-[#F5EBE1] border-[#3A2E28]/10 text-[#C4B5A8]',
    uploadBtn: 'bg-[#D48C70]/10 text-[#D48C70] hover:bg-[#D48C70]/20',
  },
  pizzeria: {
    bg: '#F5EDE4', bgHeader: 'rgba(245,237,228,0.92)',
    cardClass: 'border-[#8B6FA3]/10 bg-white shadow-[0_2px_12px_rgba(139,111,163,0.06)] hover:shadow-[0_4px_20px_rgba(139,111,163,0.12)]',
    text: 'text-[#3A2E28]', textSub: 'text-[#8B7B6E]', textHead: 'text-[#3A2E28]', textPrice: 'text-[#8B6FA3]',
    tabActive: 'bg-[#8B6FA3] text-white shadow-md', tabInactive: 'bg-white text-[#8B7B6E] border border-[#8B6FA3]/15 hover:bg-[#F0E6F6]',
    adminBtn: 'bg-[#3A2E28]/5 text-[#8B7B6E] hover:bg-[#3A2E28]/10',
    editBannerBg: '#F0E6F6', editBannerBorder: 'border-[#8B6FA3]/20', editBannerText: 'text-[#8B6FA3]',
    saveBtn: '',
    closeBtn: 'bg-[#3A2E28]/5 text-[#8B7B6E]', logoutBtn: 'bg-red-50 text-red-400',
    inputBorder: 'border-[#3A2E28]/10 focus:border-[#8B6FA3]',
    modalBg: '#F5EDE4', modalText: 'text-[#3A2E28]', modalInputClass: 'bg-white border-[#3A2E28]/10 text-[#3A2E28] focus:border-[#8B6FA3] focus:ring-2 focus:ring-[#8B6FA3]/20',
    modalBtnStyle: { background: 'linear-gradient(135deg, #8B6FA3, #B794C7)' },
    footerText: 'text-[#3A2E28]', footerSub: 'text-[#8B7B6E]', footerMuted: 'text-[#C4B5A8]',
    footerLine: 'linear-gradient(to right, transparent, #8B6FA3, transparent)',
    linkCard: 'border-[#8B6FA3]/12 bg-white shadow-sm hover:shadow-md',
    linkText: 'text-[#3A2E28]', headingFont: "'Playfair Display'", bodyFont: 'Outfit',
    ring: 'ring-white', tabsBorder: 'border-[#8B6FA3]/8', tabsBg: 'rgba(245,237,228,0.95)',
    imgPlaceholder: 'bg-[#F0E6F6] border-[#8B6FA3]/10 text-[#C4B5A8]',
    uploadBtn: 'bg-[#8B6FA3]/10 text-[#8B6FA3] hover:bg-[#8B6FA3]/20',
  },
  red: {
    bg: '#B3202B', bgHeader: 'rgba(179,32,43,0.95)',
    cardClass: 'border-white/15 bg-white/[0.08] hover:bg-white/[0.14] hover:border-white/30',
    text: 'text-white', textSub: 'text-white/70', textHead: 'text-white', textPrice: 'text-white',
    tabActive: 'bg-white text-[#B3202B] shadow-md', tabInactive: 'bg-white/10 text-white/85 border border-white/20 hover:bg-white/20',
    adminBtn: 'bg-white/10 border-white/20 text-white/80 hover:bg-white/20',
    editBannerBg: '', editBannerBorder: 'border-white/30', editBannerText: 'text-white',
    saveBtn: 'bg-emerald-600 hover:bg-emerald-700',
    closeBtn: 'bg-white/15 text-white', logoutBtn: 'bg-black/25 text-white',
    inputBorder: 'border-white/30 focus:border-white',
    modalBg: '#9E1B25', modalText: 'text-white', modalInputClass: 'bg-white/10 border-white/20 text-white focus:border-white',
    modalBtnStyle: { background: '#1a1a1a' },
    footerText: 'text-white', footerSub: 'text-white/70', footerMuted: 'text-white/50',
    footerLine: 'linear-gradient(to right, transparent, #ffffff, transparent)',
    linkCard: 'border-white/20 bg-white/10 hover:bg-white/20',
    linkText: 'text-white', headingFont: 'Outfit', bodyFont: 'Outfit',
    ring: 'ring-white/70', tabsBorder: 'border-white/15', tabsBg: 'rgba(179,32,43,0.98)',
    imgPlaceholder: 'bg-white/10 border-white/20 text-white/50',
    uploadBtn: 'bg-white/15 text-white hover:bg-white/25',
  },
  gold: {
    bg: '#0B0A08', bgHeader: 'rgba(11,10,8,0.94)',
    cardClass: 'border-[#D4A017]/20 bg-gradient-to-br from-[#171410] to-[#0F0D0A] hover:border-[#D4A017]/50 shadow-[0_2px_16px_rgba(212,160,23,0.06)]',
    text: 'text-[#F5E6C8]', textSub: 'text-[#A89878]', textHead: 'text-[#F2C94C]', textPrice: 'text-[#F2C94C]',
    tabActive: 'bg-gradient-to-r from-[#D4A017] to-[#F2C94C] text-black shadow-[0_4px_14px_rgba(212,160,23,0.35)]', tabInactive: 'bg-white/5 text-[#C9B48A] border border-[#D4A017]/20 hover:bg-[#D4A017]/15 hover:text-[#F2C94C]',
    adminBtn: 'bg-white/5 border-[#D4A017]/30 text-[#C9B48A] hover:bg-[#D4A017]/15',
    editBannerBg: '', editBannerBorder: 'border-[#D4A017]/40', editBannerText: 'text-[#F2C94C]',
    saveBtn: 'bg-emerald-600 hover:bg-emerald-700',
    closeBtn: 'bg-white/10 text-[#F5E6C8]', logoutBtn: 'bg-red-500/20 text-red-400',
    inputBorder: 'border-[#D4A017]/30 focus:border-[#F2C94C]',
    modalBg: '#141210', modalText: 'text-[#F5E6C8]', modalInputClass: 'bg-white/5 border-[#D4A017]/30 text-[#F5E6C8] focus:border-[#F2C94C]',
    modalBtnStyle: { background: 'linear-gradient(135deg, #D4A017, #F2C94C)', color: '#000' },
    footerText: 'text-[#F2C94C]', footerSub: 'text-[#A89878]', footerMuted: 'text-[#6B5F48]',
    footerLine: 'linear-gradient(to right, transparent, #D4A017, transparent)',
    linkCard: 'border-[#D4A017]/25 bg-gradient-to-r from-[#171410] to-[#0F0D0A] hover:border-[#F2C94C]/60',
    linkText: 'text-[#F5E6C8]', headingFont: "'Playfair Display'", bodyFont: 'Outfit',
    ring: 'ring-[#D4A017]/70', tabsBorder: 'border-[#D4A017]/15', tabsBg: 'rgba(11,10,8,0.97)',
    imgPlaceholder: 'bg-white/5 border-[#D4A017]/20 text-[#6B5F48]',
    uploadBtn: 'bg-[#D4A017]/15 text-[#F2C94C] hover:bg-[#D4A017]/25',
  },
  olive: {
    bg: '#FAF6EC', bgHeader: 'rgba(250,246,236,0.94)',
    cardClass: 'border-[#1F4D2E]/15 bg-white hover:border-[#B8860B]/60 shadow-[0_2px_14px_rgba(31,77,46,0.06)] hover:shadow-[0_6px_20px_rgba(31,77,46,0.12)]',
    text: 'text-[#1F3D2B]', textSub: 'text-[#6B705C]', textHead: 'text-[#1F4D2E]', textPrice: 'text-[#9A7209]',
    tabActive: 'bg-[#1F4D2E] text-[#F2D06B] shadow-[0_4px_14px_rgba(31,77,46,0.3)]', tabInactive: 'bg-white text-[#1F4D2E] border border-[#1F4D2E]/20 hover:border-[#B8860B]/60 hover:bg-[#FDF8E7]',
    adminBtn: 'bg-white border-[#1F4D2E]/20 text-[#1F4D2E] hover:bg-[#FDF8E7]',
    editBannerBg: '', editBannerBorder: 'border-[#B8860B]/40', editBannerText: 'text-[#1F4D2E]',
    saveBtn: 'bg-[#1F4D2E] hover:bg-[#163a22]',
    closeBtn: 'bg-[#1F4D2E]/10 text-[#1F4D2E]', logoutBtn: 'bg-red-50 text-red-600',
    inputBorder: 'border-[#1F4D2E]/20 focus:border-[#B8860B]',
    modalBg: '#FAF6EC', modalText: 'text-[#1F3D2B]', modalInputClass: 'bg-white border-[#1F4D2E]/20 text-[#1F3D2B] focus:border-[#B8860B]',
    modalBtnStyle: { background: '#1F4D2E', color: '#F2D06B' },
    footerText: 'text-[#1F4D2E]', footerSub: 'text-[#6B705C]', footerMuted: 'text-[#9A9A8A]',
    footerLine: 'linear-gradient(to right, transparent, #B8860B, transparent)',
    linkCard: 'border-[#1F4D2E]/15 bg-white hover:border-[#B8860B]/60',
    linkText: 'text-[#1F3D2B]', headingFont: "'Playfair Display'", bodyFont: 'Outfit',
    ring: 'ring-[#B8860B]/60', tabsBorder: 'border-[#1F4D2E]/10', tabsBg: 'rgba(250,246,236,0.97)',
    imgPlaceholder: 'bg-[#1F4D2E]/5 border-[#1F4D2E]/15 text-[#6B705C]',
    uploadBtn: 'bg-[#1F4D2E]/10 text-[#1F4D2E] hover:bg-[#1F4D2E]/20',
    tagClass: 'bg-[#1F4D2E]/8 text-[#1F4D2E] border border-[#1F4D2E]/15',
  }
};

const fadeUp = { hidden: { opacity: 0, y: 16 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.04, duration: 0.35 } }) };

const LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
];

export default function MenuPage() {
  const { slug } = useParams();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem(`menu_token_${slug}`));
  const [showLogin, setShowLogin] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem(`menu_lang_${slug}`) || 'fr');
  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const [activeCatIdx, setActiveCatIdx] = useState(0);
  const [cart, setCart] = useState(() => { try { return JSON.parse(localStorage.getItem(`menu_cart_${slug}`)) || []; } catch { return []; } });
  useEffect(() => { localStorage.setItem(`menu_cart_${slug}`, JSON.stringify(cart)); }, [cart, slug]);
  const canOrder = !!menu?.ordering_enabled && !editMode;
  const cartQty = (ci, ii) => cart.find(c => c.key === cartKey(ci, ii))?.qty || 0;
  const changeCartQty = (ci, ii, delta) => setCart(prev => {
    const key = cartKey(ci, ii);
    const existing = prev.find(c => c.key === key);
    if (!existing) return delta > 0 ? [...prev, { key, ci, ii, qty: 1, comment: '', name: menu?.categories?.[ci]?.items?.[ii]?.name }] : prev;
    const qty = existing.qty + delta;
    return qty <= 0 ? prev.filter(c => c.key !== key) : prev.map(c => c.key === key ? { ...c, qty } : c);
  });

  const changeLang = (code) => {
    setLang(code);
    localStorage.setItem(`menu_lang_${slug}`, code);
    setLangPickerOpen(false);
  };

  const fetchMenu = useCallback(() => {
    axios.get(`${API}/menu/${slug}`)
      .then(res => { setMenu(res.data); setActiveCategory(res.data.categories?.[0]?.name || null); })
      .catch(err => setError(err.response?.data?.detail || 'Menu introuvable'))
      .finally(() => setLoading(false));
  }, [slug]);
  useEffect(() => { fetchMenu(); }, [fetchMenu]);

  // Redirect to site page for pizzeria theme (unless ?digital=1 is set)
  const skipRedirect = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('digital') === '1';
  if (menu?.theme === 'pizzeria' && !skipRedirect) {
    return <Navigate to={`/site/${slug}`} replace />;
  }

  const handleLogin = async (e) => { e.preventDefault(); setLoginError(''); try { const { data } = await axios.post(`${API}/menu/${slug}/login`, { password }); localStorage.setItem(`menu_token_${slug}`, data.token); setToken(data.token); setShowLogin(false); setPassword(''); enterEditMode(); } catch (err) { setLoginError(err.response?.data?.detail || 'Erreur'); } };
  const enterEditMode = () => { setEditData(JSON.parse(JSON.stringify(menu))); setEditMode(true); };
  const handleAdminClick = () => { if (token) enterEditMode(); else setShowLogin(true); };
  const handleLogout = () => { localStorage.removeItem(`menu_token_${slug}`); setToken(null); setEditMode(false); setEditData(null); };
  const handleSave = async () => { setSaving(true); try { await axios.put(`${API}/menu/${slug}/update`, { business_name: editData.business_name, subtitle: editData.subtitle, categories: editData.categories }, { headers: { Authorization: `Bearer ${token}` } }); setMenu(editData); setEditMode(false); fetchMenu(); } catch (err) { if (err.response?.status === 401) { handleLogout(); setShowLogin(true); } else alert(err.response?.data?.detail || 'Erreur'); } finally { setSaving(false); } };

  const updateItem = (ci, ii, f, v) => { const c = [...editData.categories]; c[ci].items[ii] = { ...c[ci].items[ii], [f]: v }; setEditData({ ...editData, categories: c }); };
  const addItem = (ci) => { const c = [...editData.categories]; c[ci].items.push({ name: 'Nouveau plat', price: 0 }); setEditData({ ...editData, categories: c }); };
  const removeItem = (ci, ii) => { const c = [...editData.categories]; c[ci].items = c[ci].items.filter((_, i) => i !== ii); setEditData({ ...editData, categories: c }); };
  const addCategory = () => { setEditData({ ...editData, categories: [...(editData.categories || []), { name: 'Nouvelle categorie', items: [] }] }); };
  const removeCategory = (ci) => { if (!window.confirm('Supprimer ?')) return; setEditData({ ...editData, categories: editData.categories.filter((_, i) => i !== ci) }); };
  const updateCategoryName = (ci, name) => { const c = [...editData.categories]; c[ci] = { ...c[ci], name }; setEditData({ ...editData, categories: c }); };

  const triggerUpload = (ci, ii) => { setUploadTarget({ ci, ii }); fileInputRef.current?.click(); };
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;
    const { ci, ii } = uploadTarget;
    const key = `${ci}-${ii}`;
    setUploading(key);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/menu/${slug}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const fullUrl = data.url.startsWith('http') ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      updateItem(ci, ii, 'image', fullUrl);
    } catch (err) { alert('Erreur upload'); }
    finally { setUploading(null); setUploadTarget(null); e.target.value = ''; }
  };
  const removeImage = (ci, ii) => { updateItem(ci, ii, 'image', ''); };

  const themeKey = menu?.theme || 'dark';
  const isDark = themeKey === 'dark' || themeKey === 'red' || themeKey === 'gold';
  const t = THEMES[themeKey] || THEMES.dark;

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: t.bg }}><div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: t.bg }}><div><p className="text-5xl mb-4">🍽️</p><h2 className={`text-xl font-bold ${t.textHead} mb-2`} style={{ fontFamily: t.headingFont }}>Menu introuvable</h2><p className={t.textSub}>{error}</p></div></div>;

  // Apply language overlay (translations come from backend, indexed by lang)
  const applyLang = (rawMenu) => {
    if (!rawMenu || lang === 'fr' || editMode) return rawMenu;
    const tr = rawMenu.translations?.[lang];
    if (!tr) return rawMenu;
    const merged = { ...rawMenu };
    if (tr.tagline) merged.tagline = tr.tagline;
    if (tr.subtitle) merged.subtitle = tr.subtitle;
    if (tr.hours) merged.hours = tr.hours;
    if (tr.pricing_info) merged.pricing_info = tr.pricing_info;
    if (tr.loyalty_info) merged.loyalty_info = tr.loyalty_info;
    if (Array.isArray(tr.payment_methods) && tr.payment_methods.length) merged.payment_methods = tr.payment_methods;
    if (Array.isArray(tr.categories)) {
      merged.categories = rawMenu.categories.map((cat, ci) => {
        const tcat = tr.categories[ci];
        if (!tcat) return cat;
        return {
          ...cat,
          name: tcat.name || cat.name,
          items: (cat.items || []).map((it, ii) => {
            const tit = tcat.items?.[ii];
            if (!tit) return it;
            return { ...it, name: tit.name || it.name, description: tit.description ?? it.description, tag: tit.tag || it.tag };
          })
        };
      });
    }
    return merged;
  };

  const d = editMode ? editData : applyLang(menu);
  const categories = d?.categories || [];
  // Use index-based active category (since names change with language)
  const safeCatIdx = Math.min(activeCatIdx, categories.length - 1);
  const activeCat = categories[safeCatIdx];
  const activeItems = activeCat?.items || [];
  const activeCatIndex = safeCatIdx;
  const links = d?.links || [];
  const isRTL = lang === 'ar';

  return (
    <div className="min-h-screen" style={{ background: t.bg }}>
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />

      {/* HEADER */}
      <header className={`sticky top-0 z-50 backdrop-blur-xl border-b ${t.tabsBorder}`} style={{ background: t.bgHeader }}>
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-3">
          {d.logo_url && <img src={d.logo_url} alt={d.business_name} className={`w-11 h-11 rounded-full object-cover shadow-md ring-2 ${t.ring}`} />}
          <div className="flex-1 min-w-0">
            <h1 className={`text-lg font-bold ${t.textHead} tracking-tight`} style={{ fontFamily: t.headingFont }}>{d.business_name}</h1>
            {d.subtitle && <p className={`text-[11px] ${t.textSub} tracking-wide`} style={{ fontFamily: t.bodyFont }}>{d.subtitle}</p>}
          </div>
          {editMode ? (
            <div className="flex items-center gap-1.5">
              <button onClick={handleSave} disabled={saving} className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-white transition-all active:scale-95 ${isDark ? 'bg-emerald-600' : themeKey === 'olive' ? t.saveBtn : ''}`} style={!isDark && themeKey !== 'olive' ? { background: '#6B8F71' } : {}} data-testid="menu-save-btn">
                <Save className="w-3 h-3" />{saving ? '...' : 'Sauver'}
              </button>
              <button onClick={() => { setEditMode(false); setEditData(null); }} className={`w-7 h-7 rounded-full flex items-center justify-center ${t.closeBtn}`}><X className="w-3.5 h-3.5" /></button>
              <button onClick={handleLogout} className={`w-7 h-7 rounded-full flex items-center justify-center ${t.logoutBtn}`}><LogOut className="w-3.5 h-3.5" /></button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              {/* Language picker */}
              {menu?.translations && Object.keys(menu.translations).length > 0 && (
                <div className="relative">
                  <button onClick={() => setLangPickerOpen(v => !v)} className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[13px] font-semibold ${t.adminBtn} transition-all`} data-testid="menu-lang-btn" aria-label="Select language">
                    <span className="text-base leading-none">{LANGUAGES.find(l => l.code === lang)?.flag || '🌐'}</span>
                    <span className="uppercase text-[10px] tracking-wider">{lang}</span>
                  </button>
                  {langPickerOpen && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={() => setLangPickerOpen(false)} />
                      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className={`absolute top-full right-0 mt-2 rounded-2xl shadow-xl border ${isDark ? 'bg-[#222] border-white/10' : themeKey === 'pizzeria' ? 'bg-white border-[#8B6FA3]/15' : 'bg-white border-[#3A2E28]/10'} overflow-hidden z-40 min-w-[140px]`}>
                        {LANGUAGES.filter(l => l.code === 'fr' || menu.translations?.[l.code]).map(L => (
                          <button key={L.code} onClick={() => changeLang(L.code)} className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm hover:bg-black/5 transition-colors ${lang === L.code ? 'font-bold' : ''} ${isDark ? 'text-white hover:bg-white/10' : 'text-[#3A2E28]'}`} data-testid={`menu-lang-${L.code}`}>
                            <span className="text-base">{L.flag}</span>
                            <span style={{ fontFamily: t.bodyFont }}>{L.label}</span>
                            {lang === L.code && <span className="ml-auto text-xs text-emerald-500">✓</span>}
                          </button>
                        ))}
                      </motion.div>
                    </>
                  )}
                </div>
              )}
              <button onClick={handleAdminClick} className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] ${t.adminBtn} transition-all`} data-testid="menu-admin-btn"><Settings className="w-3 h-3" /></button>
            </div>
          )}
        </div>
      </header>

      {editMode && <div className={`border-b ${t.editBannerBorder} px-4 py-2 text-center`} style={t.editBannerBg ? { background: t.editBannerBg } : {}}><p className={`text-xs ${t.editBannerText} font-medium`}>Mode edition — modifiez puis sauvegardez</p></div>}

      {/* HERO SECTION (pizzeria theme) */}
      {!editMode && d.tagline && (
        <div className="max-w-md mx-auto px-5 pt-6 pb-3">
          <div className="text-center">
            {d.logo_url && <img src={d.logo_url} alt={d.business_name} className="w-24 h-24 rounded-full object-cover mx-auto shadow-xl ring-3 ring-white mb-4" />}
            <h2 className={`text-2xl font-black ${t.textHead} mb-2`} style={{ fontFamily: t.headingFont }}>{d.business_name}</h2>
            <p className={`text-xs ${t.textSub} leading-relaxed max-w-xs mx-auto italic mb-4`} style={{ fontFamily: t.bodyFont }}>{d.tagline}</p>
            <div className="flex flex-wrap justify-center gap-2 mb-3">
              {d.phone && (
                <a href={`tel:${d.phone}`} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${t.tabInactive}`} style={{ fontFamily: t.bodyFont }}>
                  <MapPin className="w-3 h-3" />{d.phone}
                </a>
              )}
              {d.social && (
                <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${t.tabInactive}`} style={{ fontFamily: t.bodyFont }}>
                  {d.social}
                </span>
              )}
            </div>
            {d.hours && <p className={`text-[10px] ${t.textSub} leading-relaxed`} style={{ fontFamily: t.bodyFont }}>{d.hours}</p>}
            {d.pricing_info && (
              <div className={`mt-3 inline-block px-4 py-2 rounded-2xl border ${themeKey === 'pizzeria' ? 'border-[#8B6FA3]/20' : themeKey === 'olive' ? 'border-[#1F4D2E]/20' : isDark ? 'border-amber-700/30' : 'border-[#D48C70]/20'}`} style={{ background: themeKey === 'pizzeria' ? '#F0E6F6' : themeKey === 'olive' ? '#EEF3E9' : isDark ? 'rgba(245,158,11,0.08)' : '#FFF5EE' }}>
                <p className={`text-[11px] sm:text-xs font-semibold ${t.textPrice} leading-snug`} style={{ fontFamily: t.bodyFont }}>{d.pricing_info}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ACTION BUTTONS */}
      {!editMode && links.length > 0 && (
        <div className="max-w-md mx-auto px-4 pt-5 pb-2">
          {isDark ? (
            <div className="grid grid-cols-1 gap-2">
              {links.map((link) => (
                <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer"
                  className={`flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl border ${t.linkCard} transition-all active:scale-[0.98]`}>
                  {link.icon === 'facebook' && <FacebookIcon size={22} />}
                  {link.icon === 'google' && <GoogleIcon size={22} />}
                  {link.icon === 'instagram' && <InstagramIcon size={22} />}
                  {link.icon === 'tiktok' && <TikTokIcon size={22} />}
                  {link.icon === 'snapchat' && <SnapchatIcon size={22} />}
                  {link.icon === 'game' && <span className="text-xl">🎡</span>}
                  {!['facebook', 'google', 'instagram', 'tiktok', 'snapchat', 'game'].includes(link.icon) && <ExternalLink className="w-5 h-5 text-amber-500" />}
                  <span className={`text-sm font-semibold ${t.linkText}`}>{link.label}</span>
                </a>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2.5">
                {links.filter(l => l.icon !== 'game').map((link) => (
                  <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer"
                    className={`flex items-center justify-center gap-2 py-3 px-3 rounded-2xl border ${t.linkCard} text-sm font-medium ${t.linkText} transition-all active:scale-95`} style={{ fontFamily: t.bodyFont }}>
                    {link.icon === 'facebook' && <FacebookIcon size={20} />}
                    {link.icon === 'google' && <GoogleIcon size={20} />}
                    {link.icon === 'instagram' && <InstagramIcon size={20} />}
                    {link.icon === 'tiktok' && <TikTokIcon size={20} />}
                    {link.icon === 'snapchat' && <SnapchatIcon size={20} />}
                    {!['facebook', 'google', 'instagram', 'tiktok', 'snapchat', 'game'].includes(link.icon) && <ExternalLink className="w-4 h-4 text-[#D48C70]" />}
                    <span>{link.label}</span>
                  </a>
                ))}
              </div>
              {links.filter(l => l.icon === 'game').map((link, i) => (
                <a key={`g-${i}`} href={link.url} target="_blank" rel="noopener noreferrer"
                  className="mt-2.5 flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-2xl text-white font-bold text-sm shadow-lg transition-all active:scale-95 hover:shadow-xl"
                  style={{ background: 'linear-gradient(135deg, #D48C70 0%, #E6B981 100%)', fontFamily: t.bodyFont }}>
                  <span className="text-lg">🎡</span><span>{link.label}</span>
                </a>
              ))}
            </>
          )}
        </div>
      )}

      {/* CATEGORY TABS */}
      <div className={`sticky top-[57px] z-40 border-b ${t.tabsBorder}`} style={{ background: t.tabsBg }}>
        <div className="max-w-md mx-auto overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 px-4 py-2.5">
            {categories.map((cat, ci) => {
              // Look up icon by original FR name for consistency across languages
              const frName = menu?.categories?.[ci]?.name || cat.name;
              return (
                <button key={ci} onClick={() => setActiveCatIdx(ci)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${activeCatIndex === ci ? t.tabActive : t.tabInactive}`}
                  style={{ fontFamily: t.bodyFont }} data-testid={`menu-cat-${frName}`}>
                  <span className="text-sm">{CATEGORY_ICONS[frName] || '📋'}</span>{cat.name}
                </button>
              );
            })}
            {editMode && <button onClick={addCategory} className={`flex items-center gap-1 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap ${isDark ? 'bg-emerald-600/20 text-emerald-400' : ''}`} style={!isDark ? { background: '#E8F5E9', color: '#4CAF50' } : {}}><Plus className="w-3 h-3" />Categorie</button>}
          </div>
        </div>
      </div>

      {/* ITEMS */}
      <main className={`max-w-md mx-auto px-4 py-5 ${canOrder && cart.length > 0 ? 'pb-24' : ''}`} data-testid="menu-items" dir={isRTL ? 'rtl' : 'ltr'}>
        <AnimatePresence mode="wait">
          <motion.div key={`${activeCatIndex}-${lang}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
            <div className="flex items-center justify-between mb-4">
              {editMode ? (
                <input value={activeCat?.name || ''} onChange={e => { updateCategoryName(activeCatIndex, e.target.value); }}
                  className={`text-2xl font-bold ${t.textHead} bg-transparent border-b-2 ${isDark ? 'border-amber-600/50' : themeKey === 'olive' ? 'border-[#B8860B]' : 'border-[#D48C70]'} outline-none flex-1 mr-2 pb-1`} style={{ fontFamily: t.headingFont }} />
              ) : (
                <h2 className={`text-2xl font-bold ${t.textHead} flex items-center gap-2`} style={{ fontFamily: t.headingFont }}>
                  <span className="text-2xl">{CATEGORY_ICONS[menu?.categories?.[activeCatIndex]?.name] || '📋'}</span>{activeCat?.name}
                </h2>
              )}
              {editMode && (
                <div className="flex gap-1.5">
                  <button onClick={() => addItem(activeCatIndex)} className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${isDark ? 'bg-emerald-600/20 text-emerald-400' : ''}`} style={!isDark ? { background: '#E8F5E9', color: '#4CAF50' } : {}}><Plus className="w-3 h-3" />Plat</button>
                  <button onClick={() => removeCategory(activeCatIndex)} className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs ${isDark ? 'bg-red-500/20 text-red-400' : ''}`} style={!isDark ? { background: '#FFEBEE', color: '#EF5350' } : {}}><Trash2 className="w-3 h-3" /></button>
                </div>
              )}
            </div>
            <div className="space-y-3">
              {activeItems.map((item, i) => {
                const hasImage = !!item.image;
                const isUploading = uploading === `${activeCatIndex}-${i}`;
                return (
                  <motion.div key={i} custom={i} initial="hidden" animate="visible" variants={fadeUp}
                    className={`rounded-2xl border overflow-hidden transition-all ${t.cardClass || ''}`}>
                    {/* Image display (public mode) */}
                    {!editMode && hasImage && (
                      <div className="aspect-[16/10] w-full overflow-hidden">
                        <img src={item.image} alt={item.name} className="w-full h-full object-contain bg-white/50" loading="lazy" />
                      </div>
                    )}
                    <div className="p-4">
                      {editMode ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <input value={item.name} onChange={e => updateItem(activeCatIndex, i, 'name', e.target.value)}
                              className={`flex-1 bg-transparent text-[15px] font-medium ${t.text} border-b ${t.inputBorder} outline-none pb-0.5 min-w-0`} style={{ fontFamily: t.headingFont }} />
                            <input type="number" step="0.01" value={item.price} onChange={e => updateItem(activeCatIndex, i, 'price', parseFloat(e.target.value) || 0)}
                              className={`w-16 bg-transparent text-sm font-bold ${t.textPrice} text-right border-b ${t.inputBorder} outline-none`} style={{ fontFamily: t.bodyFont }} />
                            <span className={`text-xs ${t.textSub}`}>€</span>
                            <button onClick={() => removeItem(activeCatIndex, i)} className={`p-1 rounded ${isDark ? 'hover:bg-red-500/20 text-red-400/60 hover:text-red-400' : 'hover:bg-red-50 text-red-300 hover:text-red-500'}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                          <input value={item.description || ''} onChange={e => updateItem(activeCatIndex, i, 'description', e.target.value)} placeholder="Description / ingrédients" data-testid="item-description-input"
                            className={`w-full bg-transparent text-xs ${t.textSub} border-b ${t.inputBorder} outline-none pb-0.5`} style={{ fontFamily: t.bodyFont }} />
                          <input value={item.tag || ''} onChange={e => updateItem(activeCatIndex, i, 'tag', e.target.value)} placeholder="Étiquette (ex : Végétarien, Bœuf, Épicé…)" data-testid="item-tag-input"
                            className={`w-full bg-transparent text-xs ${t.textSub} border-b ${t.inputBorder} outline-none pb-0.5`} style={{ fontFamily: t.bodyFont }} />
                          {/* Image upload area */}
                          <div className="flex items-center gap-2">
                            {hasImage ? (
                              <div className="relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                                <img src={item.image} alt="" className="w-full h-full object-cover" />
                                <button onClick={() => removeImage(activeCatIndex, i)} className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="w-2.5 h-2.5" /></button>
                              </div>
                            ) : null}
                            <button onClick={() => triggerUpload(activeCatIndex, i)} disabled={isUploading}
                              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${t.uploadBtn}`}>
                              {isUploading ? <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                              {isUploading ? 'Upload...' : hasImage ? 'Changer' : 'Ajouter photo'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-between items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <p className={`text-[15px] font-semibold ${t.text}`} style={{ fontFamily: t.headingFont }}>{item.name}</p>
                            {item.description && <p className={`text-xs ${t.textSub} mt-1 leading-relaxed`} style={{ fontFamily: t.bodyFont }}>{item.description}</p>}
                            {item.tag && <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${t.tagClass || t.tabInactive}`} data-testid="item-tag">{item.tag}</span>}
                          </div>
                          {(item.price > 0 || item.price_large > 0) && (
                            <div className="flex flex-col items-end gap-2 shrink-0">
                              <span className={`text-sm font-bold ${t.textPrice} whitespace-nowrap pt-0.5`} style={{ fontFamily: t.bodyFont }}>
                                {item.price_large ? `${item.price.toFixed(2)}€ / ${item.price_large.toFixed(2)}€` : `${item.price.toFixed(2)}€`}
                              </span>
                              {canOrder && item.price > 0 && (
                                cartQty(activeCatIndex, i) > 0 ? (
                                  <div className={`flex items-center gap-1 rounded-full ${t.tabActive}`} data-testid={`item-qty-control-${activeCatIndex}-${i}`}>
                                    <button onClick={() => changeCartQty(activeCatIndex, i, -1)} className="w-8 h-8 flex items-center justify-center" data-testid={`item-dec-${activeCatIndex}-${i}`}><Minus className="w-3.5 h-3.5" /></button>
                                    <span className="text-sm font-bold w-4 text-center" data-testid={`item-qty-${activeCatIndex}-${i}`}>{cartQty(activeCatIndex, i)}</span>
                                    <button onClick={() => changeCartQty(activeCatIndex, i, 1)} className="w-8 h-8 flex items-center justify-center" data-testid={`item-inc-${activeCatIndex}-${i}`}><Plus className="w-3.5 h-3.5" /></button>
                                  </div>
                                ) : (
                                  <button onClick={() => changeCartQty(activeCatIndex, i, 1)} className={`flex items-center gap-1 px-3 h-8 rounded-full text-xs font-semibold transition-all active:scale-95 ${t.tabInactive}`} style={{ fontFamily: t.bodyFont }} data-testid={`item-add-${activeCatIndex}-${i}`}>
                                    <Plus className="w-3.5 h-3.5" />{(CART_I18N[lang] || CART_I18N.fr).add}
                                  </button>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>
      </main>

      {/* FIDELITE & PAIEMENTS */}
      {!editMode && (d.loyalty_info || d.payment_methods?.length > 0) && (
        <section className="max-w-md mx-auto px-4 pt-2 pb-4 space-y-3" data-testid="menu-info-cards">
          {d.loyalty_info && (
            <div className="relative rounded-2xl overflow-hidden p-5 shadow-lg" style={{ background: themeKey === 'pizzeria' ? 'linear-gradient(135deg, #8B6FA3 0%, #B794C7 100%)' : isDark ? 'linear-gradient(135deg, #B45309 0%, #D97706 100%)' : 'linear-gradient(135deg, #D48C70 0%, #E6B981 100%)' }}>
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10" />
              <div className="relative">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm mb-2">
                  <Star className="w-3 h-3 text-white" />
                  <span className="text-[10px] font-bold text-white uppercase tracking-wider" style={{ fontFamily: t.bodyFont }}>Carte de fidélité</span>
                </div>
                <p className="text-base sm:text-lg font-black text-white leading-tight" style={{ fontFamily: t.headingFont }}>{d.loyalty_info}</p>
                <p className="text-[11px] text-white/80 mt-1.5" style={{ fontFamily: t.bodyFont }}>Demandez votre carte dès aujourd&apos;hui !</p>
              </div>
            </div>
          )}
          {d.payment_methods?.length > 0 && (
            <div className={`rounded-2xl p-5 border ${t.cardClass || ''}`}>
              <p className={`text-[10px] uppercase tracking-wider font-bold ${t.textPrice} mb-2.5`} style={{ fontFamily: t.bodyFont }}>Moyens de paiement</p>
              <div className="flex flex-wrap gap-1.5">
                {d.payment_methods.map((pm) => (
                  <span key={pm} className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold ${t.text}`} style={{ background: themeKey === 'pizzeria' ? '#F5EDE4' : isDark ? 'rgba(255,255,255,0.06)' : '#F5EBE1', fontFamily: t.bodyFont }}>
                    {pm}
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* FOOTER */}
      <footer className="max-w-md mx-auto px-4 py-10 text-center mt-4">
        <div className="w-16 h-[1px] mx-auto mb-6" style={{ background: t.footerLine }} />
        {d.logo_url && <img src={d.logo_url} alt="" className={`w-16 h-16 rounded-full mx-auto mb-3 shadow-md ring-2 ${t.ring}`} />}
        <p className={`text-base font-bold ${t.footerText}`} style={{ fontFamily: t.headingFont }}>{d.business_name}</p>
        <p className={`text-xs ${t.footerSub} mt-1`} style={{ fontFamily: t.bodyFont }}>{d.subtitle}</p>
        {d.address && <p className={`text-xs ${t.footerSub} mt-1.5 flex items-center justify-center gap-1`} style={{ fontFamily: t.bodyFont }}><MapPin className="w-3 h-3" />{d.address}</p>}
        {d.phone && <p className={`text-xs ${t.footerSub} mt-1`} style={{ fontFamily: t.bodyFont }}>{d.phone}</p>}
        {d.hours && <p className={`text-[10px] ${t.footerMuted} mt-2 leading-relaxed max-w-xs mx-auto`} style={{ fontFamily: t.bodyFont }}>{d.hours}</p>}
        <p className={`text-[10px] ${t.footerMuted} mt-6`} style={{ fontFamily: t.bodyFont }}>Menu digital par Spin Review</p>
      </footer>

      {/* PANIER / COMMANDE */}
      {canOrder && <OrderCart slug={slug} rawMenu={menu} displayMenu={d} cart={cart} setCart={setCart} lang={lang} t={t} isDark={isDark} />}

      {/* LOGIN MODAL */}
      {showLogin && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className={`rounded-3xl border ${isDark ? 'border-amber-900/30' : 'border-[#3A2E28]/10'} p-7 w-full max-w-sm shadow-2xl`} style={{ background: t.modalBg }}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2.5"><Lock className={`w-5 h-5 ${isDark ? 'text-amber-500' : 'text-[#D48C70]'}`} /><h3 className={`text-lg font-bold ${t.modalText}`} style={{ fontFamily: t.headingFont }}>Espace proprietaire</h3></div>
              <button onClick={() => { setShowLogin(false); setLoginError(''); }} className={isDark ? 'text-gray-400 hover:text-white' : 'text-[#7A685D] hover:text-[#3A2E28]'}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className={`text-xs ${t.textSub} block mb-1.5`} style={{ fontFamily: t.bodyFont }}>Mot de passe</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} autoFocus required
                  className={`w-full px-4 py-3 rounded-xl outline-none text-sm ${t.modalInputClass}`}
                  style={{ fontFamily: t.bodyFont }} placeholder="Entrez votre mot de passe" data-testid="menu-login-password" />
              </div>
              {loginError && <p className="text-xs text-red-500">{loginError}</p>}
              <button type="submit" className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-all active:scale-95 shadow-lg"
                style={t.modalBtnStyle} data-testid="menu-login-submit">Se connecter</button>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
